import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const todayIso = () => new Date().toISOString().slice(0, 10);
const plusDays = (n) => {
    const d = new Date(); d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
};
const fmtMoney = (cents) => {
    const v = Number(cents || 0) / 100;
    return v.toLocaleString("en-US", { style: "currency", currency: "USD" });
};

export default function EndRentalModal({ open, onOpenChange, rental, onSuccess }) {
    const [endDate, setEndDate] = useState(todayIso());
    const [reason, setReason] = useState("");
    // Phase DP-R1 follow-up: force the admin to explicitly pick a credit
    // action when there IS an unused portion. Default null (no preselect)
    // so the submit stays disabled until they make the call.
    const [creditAction, setCreditAction] = useState(null);
    // Phase DP-R3: separate decision for deposit surplus.
    const [depositRefundAction, setDepositRefundAction] = useState(null);
    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [previewErr, setPreviewErr] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const debounceRef = useRef(null);

    const minDate = useMemo(() => {
        const sd = rental?.start_date ? String(rental.start_date).slice(0, 10) : null;
        const candidates = [sd, preview?.last_invoice_period_end]
            .filter(Boolean).sort();
        return candidates[candidates.length - 1] || sd || todayIso();
    }, [rental, preview]);
    const maxDate = useMemo(() => plusDays(90), []);

    useEffect(() => {
        if (!open) return;
        setEndDate(todayIso());
        setReason("");
        setCreditAction(null);
        setDepositRefundAction(null);
        setPreview(null);
        setPreviewErr(null);
    }, [open]);

    // Debounced preview fetch.
    useEffect(() => {
        if (!open || !rental?.id) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setPreviewing(true);
            setPreviewErr(null);
            try {
                const { data } = await api.post(
                    `/admin/rentals/${rental.id}/end/preview`,
                    { end_date: endDate },
                );
                setPreview(data);
            } catch (err) {
                setPreviewErr(formatApiError(err));
                setPreview(null);
            } finally {
                setPreviewing(false);
            }
        }, 300);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
    }, [open, endDate, rental?.id]);

    const unusedCents = preview?.unused_amount_cents || 0;
    const stripeEligible = !!(preview?.eligible_credit_actions || []).includes("stripe_refund");
    const depositSurplus = Math.max(preview?.deposit_net_cents || 0, 0);
    const depositHeld = preview?.deposit_held_cents || 0;
    const depositApplied = Math.min(depositHeld, preview?.total_owed_cents || 0);
    const depositShortfall = Math.max((preview?.total_owed_cents || 0) - depositHeld, 0);
    const depositStripeEligible = !!(preview?.eligible_deposit_refund_actions || []).includes("stripe_refund");
    const requiresDepositDecision = !!preview?.requires_deposit_refund_decision;
    const reasonTrim = reason.trim();
    const reasonValid = reasonTrim.length > 0 && reasonTrim.length <= 500;
    // When there's an unused portion the admin MUST explicitly pick a
    // credit action; ``null`` (the default) is intentionally not valid.
    const creditValid = unusedCents <= 0
        ? true
        : ["forfeit", "credit_memo", "stripe_refund"].includes(creditAction || "")
            && (creditAction !== "stripe_refund" || stripeEligible);
    const depositRefundValid = !requiresDepositDecision
        ? true
        : ["forfeit", "credit_memo", "stripe_refund"].includes(depositRefundAction || "")
            && (depositRefundAction !== "stripe_refund" || depositStripeEligible);
    const showCreditHint = unusedCents > 0 && !creditAction;
    const showDepositHint = requiresDepositDecision && !depositRefundAction;
    const canSubmit = reasonValid && creditValid && depositRefundValid
        && !previewErr && !!preview && preview.inspection_complete;

    const onSubmit = async () => {
        setSubmitting(true);
        try {
            const body = {
                end_date: endDate,
                end_reason: reasonTrim,
                end_type: "terminated",
                credit_action: unusedCents > 0 ? creditAction : "forfeit",
                deposit_refund_action: requiresDepositDecision ? depositRefundAction : null,
            };
            await api.post(`/admin/rentals/${rental.id}/end`, body);
            toast.success("Rental ended");
            setConfirmOpen(false);
            onOpenChange(false);
            onSuccess?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent data-testid="end-rental-dialog" className="max-w-xl">
                    <DialogHeader>
                        <DialogTitle>End Rental</DialogTitle>
                        <DialogDescription>
                            Stops the billing schedule, generates a prorated final invoice,
                            and notifies the customer. Super admin can undo this within 24h.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-1.5">
                            <Label>End date</Label>
                            <Input type="date" value={endDate} min={minDate} max={maxDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                data-testid="end-rental-date-picker" />
                            <p className="text-[11px] text-slate-500">
                                Min: {minDate} · Max: {maxDate}
                            </p>
                        </div>

                        <div className="space-y-1.5">
                            <Label>Reason <span className="text-red-500">*</span></Label>
                            <Textarea rows={3} value={reason}
                                onChange={(e) => setReason(e.target.value.slice(0, 600))}
                                placeholder="Why is this rental ending? (e.g. customer cancelled, equipment recall, paid out early)"
                                data-testid="end-rental-reason" />
                            <p className="text-[11px] text-slate-500" data-testid="end-rental-reason-counter">
                                {reasonTrim.length}/500
                            </p>
                        </div>

                        <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3.5 space-y-2 text-sm"
                            data-testid="end-rental-preview">
                            {previewing && (
                                <p className="text-slate-500 inline-flex items-center gap-1.5">
                                    <Loader2 size={14} className="animate-spin" /> Computing preview…
                                </p>
                            )}
                            {previewErr && (
                                <p className="text-red-700 inline-flex items-center gap-1.5">
                                    <AlertTriangle size={14} /> {previewErr}
                                </p>
                            )}
                            {preview && !previewErr && !preview.inspection_complete && (
                                <p className="text-red-700 inline-flex items-start gap-1.5"
                                    data-testid="end-rental-inspection-required">
                                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                                    <span>
                                        Equipment inspection required before terminating this rental.{" "}
                                        <button type="button"
                                            className="underline font-medium"
                                            data-testid="end-rental-inspection-cta"
                                            onClick={() => {
                                                onOpenChange(false);
                                                setTimeout(() => {
                                                    document.querySelector('[data-testid="inspection-card"]')
                                                        ?.scrollIntoView({ behavior: "smooth", block: "center" });
                                                }, 80);
                                            }}>
                                            Complete inspection →
                                        </button>
                                    </span>
                                </p>
                            )}
                            {preview && !previewErr && preview.inspection_complete && (
                                <p className="text-emerald-700 inline-flex items-center gap-1.5 text-xs"
                                    data-testid="end-rental-inspection-ok">
                                    <CheckCircle2 size={12} /> Inspection completed.
                                </p>
                            )}
                            {preview && !previewErr && preview.manager_handoff_warning && (
                                <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 flex items-start gap-1.5 text-xs"
                                    data-testid="end-rental-manager-handoff-warning">
                                    <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                                    <span>{preview.manager_handoff_warning}</span>
                                </p>
                            )}
                            {preview && !previewErr && (
                                <>
                                    <p className="text-slate-700" data-testid="end-rental-preview-final">
                                        <span className="font-semibold text-slate-900">Final invoice:</span>{" "}
                                        {fmtMoney(preview.prorated_amount_cents)} for{" "}
                                        <span className="font-semibold">{preview.prorated_days}</span> day
                                        {preview.prorated_days === 1 ? "" : "s"}{" "}
                                        ({preview.prorated_period_start} → {preview.prorated_period_end})
                                    </p>
                                    {preview.damages_count > 0 && (
                                        <p className="text-slate-700" data-testid="end-rental-preview-damages">
                                            <span className="font-semibold text-slate-900">Damages invoice:</span>{" "}
                                            {fmtMoney(preview.damages_total_cents)} for{" "}
                                            <span className="font-semibold">{preview.damages_count}</span> item
                                            {preview.damages_count === 1 ? "" : "s"} (separate invoice, tax-free)
                                        </p>
                                    )}
                                    {depositHeld > 0 && (
                                        <p className="text-slate-700" data-testid="end-rental-preview-deposit">
                                            <span className="font-semibold text-slate-900">Security deposit:</span>{" "}
                                            {fmtMoney(depositHeld)} held — {fmtMoney(depositApplied)} will be applied
                                            {depositSurplus > 0 && (
                                                <>, <strong>{fmtMoney(depositSurplus)}</strong> surplus to refund</>
                                            )}
                                            {depositShortfall > 0 && (
                                                <>, customer still owes <strong>{fmtMoney(depositShortfall)}</strong></>
                                            )}.
                                        </p>
                                    )}
                                    {preview.unused_days > 0 ? (
                                        <p className="text-slate-700" data-testid="end-rental-preview-unused">
                                            <span className="font-semibold text-slate-900">Already-billed unused portion:</span>{" "}
                                            {fmtMoney(preview.unused_amount_cents)} ·{" "}
                                            <span className="font-semibold">{preview.unused_days}</span> day
                                            {preview.unused_days === 1 ? "" : "s"} past the new end date
                                            on invoice <strong>{preview.last_invoice_number || "—"}</strong>.
                                        </p>
                                    ) : (
                                        <p className="text-emerald-700 inline-flex items-center gap-1.5">
                                            <CheckCircle2 size={13} /> No over-billed portion — nothing to refund or credit.
                                        </p>
                                    )}
                                    {preview.rate_source && (
                                        <p className="text-[11px] text-slate-500">
                                            Rate source: <code>{preview.rate_source}</code>
                                        </p>
                                    )}
                                </>
                            )}
                        </div>

                        {preview && (preview.unused_amount_cents || 0) > 0 && (
                            <div className="space-y-2" data-testid="end-rental-credit-action">
                                <Label>How should the unused portion ({fmtMoney(unusedCents)}) be handled?</Label>
                                <div className="space-y-1.5">
                                    <CreditChoice value="forfeit" current={creditAction} onChange={setCreditAction}
                                        label="Forfeit (customer is not refunded)" />
                                    <CreditChoice value="credit_memo" current={creditAction} onChange={setCreditAction}
                                        label="Issue credit memo for future invoice" />
                                    <CreditChoice value="stripe_refund" current={creditAction} onChange={setCreditAction}
                                        disabled={!stripeEligible}
                                        label={`Issue Stripe refund on ${preview.last_invoice_number || "last invoice"}`}
                                        tooltip={!stripeEligible ? "Last invoice was not paid via Stripe" : undefined} />
                                </div>
                                {showCreditHint && (
                                    <p className="text-xs text-red-700 inline-flex items-center gap-1.5"
                                        data-testid="end-rental-credit-hint">
                                        <AlertTriangle size={12} /> Select a credit action to continue.
                                    </p>
                                )}
                            </div>
                        )}

                        {requiresDepositDecision && (
                            <div className="space-y-2" data-testid="end-rental-deposit-refund-action">
                                <Label>How should the deposit surplus ({fmtMoney(depositSurplus)}) be handled?</Label>
                                <div className="space-y-1.5">
                                    <CreditChoice value="forfeit" current={depositRefundAction} onChange={setDepositRefundAction}
                                        label="Forfeit (deposit surplus stays with the company)" />
                                    <CreditChoice value="credit_memo" current={depositRefundAction} onChange={setDepositRefundAction}
                                        label="Issue credit memo for future invoice" />
                                    <CreditChoice value="stripe_refund" current={depositRefundAction} onChange={setDepositRefundAction}
                                        disabled={!depositStripeEligible}
                                        label="Issue Stripe refund (requires credit-card deposit)"
                                        tooltip={!depositStripeEligible ? "Deposit was not collected via credit card" : undefined} />
                                </div>
                                {showDepositHint && (
                                    <p className="text-xs text-red-700 inline-flex items-center gap-1.5"
                                        data-testid="end-rental-deposit-refund-hint">
                                        <AlertTriangle size={12} /> Select a deposit refund action to continue.
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => onOpenChange(false)}
                            data-testid="end-rental-cancel">Cancel</Button>
                        <Button disabled={!canSubmit} onClick={() => setConfirmOpen(true)}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="end-rental-submit">End Rental</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <DialogContent data-testid="end-rental-confirm-dialog" className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>End rental on {endDate}?</DialogTitle>
                        <DialogDescription>
                            This stops billing and creates a final invoice for{" "}
                            <strong>{fmtMoney(preview?.prorated_amount_cents || 0)}</strong>.
                            Super admin can undo within 24 hours.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmOpen(false)}
                            data-testid="end-rental-confirm-back">Back</Button>
                        <Button onClick={onSubmit} disabled={submitting}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="end-rental-confirm-yes">
                            {submitting && <Loader2 size={14} className="animate-spin mr-1.5" />}
                            Yes, end rental
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

function CreditChoice({ value, current, onChange, label, disabled, tooltip }) {
    return (
        <label className={`flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer text-sm ${
            disabled
                ? "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
                : current === value
                    ? "border-blue-300 bg-blue-50/60"
                    : "border-slate-200 hover:bg-slate-50"
        }`}
            title={tooltip || ""}
            data-testid={`end-rental-credit-${value}`}>
            <input type="radio" name="dpr1-credit-action" value={value}
                checked={current === value} disabled={disabled}
                onChange={() => onChange(value)} className="mt-0.5" />
            <span>
                {label}
                {disabled && tooltip && (
                    <span className="block text-[11px] text-slate-400 italic">{tooltip}</span>
                )}
            </span>
        </label>
    );
}
